"use client"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createTenantUser, getUserDetail, getUserPermissions, getUsers, searchTenantUsers, updateTenantUser, deleteTenantUser, deleteSAUser, getSAUserDetail, updateSAUser } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetTenantUsers = (page, pageSize, enabled = true) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["tenant-users", page, pageSize],
    queryFn: () => getUsers(page, pageSize),
    enabled: enabled,
  });

  return {
    data: data?.data ?? data,
    pagination: data?.meta?.pagination ?? null,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};


export const useSearchTenantUsers = (searchStr, enabled = false) => {
  const { data, isLoading, isFetching, status, isError, error, isSuccess } = useQuery({
    queryKey: ["tenant-users-search", searchStr],
    queryFn: () => searchTenantUsers(searchStr),
    enabled: enabled && searchStr.length >= 2,
    staleTime: 60 * 1000, // 1 minute
  });

  return {
    data: data?.data ?? data,
    pagination: data?.pagination ?? null,
    status,
    isLoading,
    isFetching,
    isError,
    error,
    isSuccess,
  };
};


export const useGetUserPermissions = () => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["user-permissions"],
    queryFn: () => getUserPermissions(),
    // Add caching for better performance
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetTenantUserDetail = (userId) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["user-detail-tenant-user", userId],
    queryFn: () => getUserDetail(userId),
    enabled: !!userId,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetSAUserDetail = (userId, tenantId = null) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["sa-user-detail", userId, tenantId],
    queryFn: () => getSAUserDetail(userId, tenantId),
    enabled: !!userId,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useCreateTenantUser = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (data) => createTenantUser(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["tenant-users"] });

      showNotification('success', 'User created successfully!');
      navigate('/users');

    },
    onError: (error) => {
      let errorMessage = 'Error creating user. Please try again.';

      // Check for error message in response data
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });

  return {
    ...mutation,
    createWorkspaceFn: mutation.mutate,
  };
};

export const useUpdateTenantUser = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateTenantUser(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tenant-users"] });
      await queryClient.invalidateQueries({ queryKey: ["user-detail-tenant-user"] });
      showNotification('success', 'User updated successfully!');
      // window.location.reload();
    },
    onError: (error) => {
      console.error("Error updating user:", error);

      // Show error notification
      let errorMessage = 'Error updating user. Please try again.';

      // Check for error message in response data
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteTenantUser = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (userId) => deleteTenantUser(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tenant-users"] });

      // Show success notification
      showNotification('success', 'User deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting user:", error);

      // Show error notification
      let errorMessage = 'Error deleting user. Please try again.';

      // Check for error message in response data
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};
export const useDeleteSAUser = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (userId) => deleteSAUser(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sa-users"] });
      showNotification('success', 'User deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting user:", error);
      let errorMessage = 'Error deleting user. Please try again.';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });
};

export const useUpdateSAUser = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, tenantId, data }) => updateSAUser(id, tenantId, data),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["sa-user-detail", variables.id, variables.tenantId] });
      showNotification('success', 'User updated successfully!');
    },
    onError: (error) => {
      console.error("Error updating user:", error);
      let errorMessage = 'Error updating user. Please try again.';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });
};
